import { useState } from "react";
import { GripVertical, MoveRight, Send } from "lucide-react";
import { VehicleBadge } from "@/components/VehicleBadge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const UnitChip = ({ label, count, payload, targets, onMove, testid, strong }) => (
  <span draggable onDragStart={(e) => { e.dataTransfer.setData("text/plain", JSON.stringify(payload)); e.dataTransfer.effectAllowed = "move"; }}
    className={`inline-flex cursor-grab items-center gap-1 rounded-full border py-0.5 pl-1.5 pr-1 text-xs transition-colors hover:border-[#D96B43] active:cursor-grabbing ${strong ? "border-[#0F382C]/30 bg-white font-semibold text-[#0F382C]" : "border-[#E7E2D6] bg-white text-foreground"}`}
    data-testid={testid}>
    <GripVertical className="h-3 w-3 text-muted-foreground" />{label}<span className="font-mono text-muted-foreground">{count}</span>
    <DropdownMenu>
      <DropdownMenuTrigger className="rounded-full p-0.5 hover:bg-[#F3EFE6]" aria-label="Move to" data-testid={`${testid}-move`}><MoveRight className="h-3 w-3" /></DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel className="text-xs">Move to</DropdownMenuLabel>
        {targets.filter((t) => t.id !== payload.from_partner_id).map((t) => (
          <DropdownMenuItem key={t.id || "none"} onClick={() => onMove({ ...payload, to_partner_id: t.id })} data-testid={`${testid}-to-${t.id || "none"}`}>{t.name}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  </span>
);

export const PlanRouteCard = ({ route, idx, cap, targets, onMove, vehicle, onTest }) => {
  const [over, setOver] = useState(false);
  const pid = route.partner_id || null;
  const max = route.cap ?? cap;
  const drop = (e) => {
    e.preventDefault(); setOver(false);
    const p = JSON.parse(e.dataTransfer.getData("text/plain") || "{}");
    if (p.plan_key && (p.from_partner_id || null) !== pid) onMove({ ...p, to_partner_id: pid });
  };
  const tone = !pid ? "border-rose-200 bg-rose-50" : route.over ? "border-rose-300 bg-[#FBF9F4]" : "border-[#E7E2D6] bg-[#FBF9F4]";
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}
      className={`rounded-xl border p-3 transition-shadow ${tone} ${over ? "ring-2 ring-[#D96B43] shadow-md" : ""}`} data-testid={`plan-route-${idx}`}>
      <div className="flex justify-between gap-2 font-semibold">
        <span className="flex flex-wrap items-center gap-1.5" data-testid={`plan-route-${idx}-partner`}>{route.partner || "Unassigned"}<VehicleBadge vehicle={vehicle} testid={`plan-route-${idx}-vehicle`} /></span>
        <span className={`font-mono ${route.over ? "text-rose-600" : ""}`} data-testid={`plan-route-${idx}-count`}>{route.count}/{max} drops · {route.km} km</span>
      </div>
      {pid && <button type="button" onClick={() => onTest(pid)} className="mt-1 inline-flex items-center gap-1 text-xs text-[#D96B43] hover:underline" data-testid={`plan-route-${idx}-test`}><Send className="h-3 w-3" />Test on my WhatsApp</button>}
      {route.over && <div className="mt-1 text-xs font-medium text-rose-600" data-testid={`plan-route-${idx}-over`}>Over limit by {route.count - max}</div>}
      {route.societies.length === 0 && <div className="mt-2 rounded-lg border border-dashed border-[#E7E2D6] p-3 text-center text-xs text-muted-foreground">Drop a society or tower here</div>}
      <div className="mt-2 space-y-1.5">
        {route.societies.map((s, j) => {
          const base = { plan_key: s.key, from_partner_id: pid };
          const tid = `plan-chip-${idx}-${j}`;
          const towers = (s.tower_counts || []).filter((t) => t.tower !== "-");
          return (
            <div key={`${s.key}-${j}`} className="flex flex-wrap items-center gap-1">
              <UnitChip strong label={s.name} count={s.count} payload={{ ...base, towers: [] }} targets={targets} onMove={onMove} testid={tid} />
              {towers.length > 1 && towers.map((t) => (
                <UnitChip key={t.tower} label={`T ${t.tower}`} count={t.count} payload={{ ...base, towers: [t.tower] }} targets={targets} onMove={onMove} testid={`${tid}-${t.tower}`} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};
